/**
 * <CompletionSeal /> — Step 6 of the Absorption Flow (NEW).
 *
 * 10–15s soft-landing close. Minimal text, single permitted gold halo
 * (this is a milestone moment — design system reserves gold for these).
 * Auto-advances to the night step after ~13s; tap to continue earlier.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  JOURNEY_PALETTE as P,
  JOURNEY_FONT_HEAD,
  JOURNEY_FONT_LABEL,
} from "./journeyTheme";

const SEAL_LINES = [
  { at: 0,  text: "That's enough." },
  { at: 5,  text: "It's integrating." },
];

const AUTO_ADVANCE_MS = 13_000;

export default function CompletionSeal({ onContinue }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => onContinue?.(), AUTO_ADVANCE_MS);
    return () => clearTimeout(t);
  }, [onContinue]);

  const line = SEAL_LINES.reduce(
    (cur, l) => (elapsed >= l.at ? l : cur),
    SEAL_LINES[0]
  );

  return (
    <div
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
      style={{ background: P.bgWash }}
      data-testid="completion-seal"
      onClick={() => {
        if (elapsed >= 4) onContinue?.();
      }}
    >
      {/* Single permitted gold halo — milestone moment */}
      <motion.div
        className="absolute"
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.85 }}
        transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }}
        style={{
          width: 220,
          height: 220,
          borderRadius: 999,
          background:
            "radial-gradient(circle, rgba(212,184,126,0.32) 0%, rgba(212,184,126,0.08) 55%, rgba(212,184,126,0) 80%)",
          filter: "blur(2px)",
        }}
        aria-hidden
      />
      <motion.div
        className="absolute"
        animate={{ scale: [1, 1.18, 1], opacity: [0.5, 0.85, 0.5] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        style={{
          width: 96,
          height: 96,
          borderRadius: 999,
          background:
            "radial-gradient(circle at 50% 45%, rgba(255,250,238,0.95) 0%, rgba(212,184,126,0.55) 55%, rgba(212,184,126,0) 100%)",
          boxShadow: "0 0 60px rgba(212,184,126,0.45)",
        }}
        aria-hidden
      />

      <p
        className="absolute top-14 text-[10px] uppercase tracking-[0.42em]"
        style={{ color: P.accentGold, fontFamily: JOURNEY_FONT_LABEL, fontWeight: 500 }}
      >
        Complete
      </p>

      <div className="relative" style={{ marginTop: 280 }}>
        <AnimatePresence mode="wait">
          <motion.p
            key={line.text}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 0.95, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center italic"
            style={{
              color: "#FFFFFF",
              fontFamily: JOURNEY_FONT_HEAD,
              fontWeight: 300,
              fontSize: "clamp(24px, 4.6vw, 34px)",
              letterSpacing: "-0.005em",
              textShadow: "0 4px 24px rgba(212,184,126,0.30)",
            }}
            data-testid="seal-line"
          >
            {line.text}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  );
}
